import React, { useState, useMemo } from 'react';
import { Search, ChevronDown, ChevronRight, ArrowUpDown, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

// Replaces the four summary cards on the Overview. At ~100 active accounts (and 262
// connected) the cards gave a count but no route to the account behind it — this ranks
// the portfolio worst-first and makes it searchable.

const STATUS_GROUPS = [
  { key: 'critical', label: 'Critical', color: '#ef4444', Icon: XCircle, defaultOpen: true },
  { key: 'warning', label: 'Needs Attention', color: '#eab308', Icon: AlertTriangle, defaultOpen: true },
  { key: 'healthy', label: 'Healthy', color: '#22c55e', Icon: CheckCircle2, defaultOpen: false },
];

const COLUMNS = [
  { key: 'name', label: 'Account', numeric: false },
  { key: 'health_score', label: 'Health', numeric: true },
  { key: 'spend', label: 'Spend', numeric: true },
  { key: 'leads', label: 'Leads', numeric: true },
  { key: 'cpl', label: 'CPL', numeric: true },
  { key: 'campaign_count', label: 'Campaigns', numeric: true },
];

const inr = (n) =>
  n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : n >= 1000 ? `₹${(n / 1000).toFixed(1)}K` : `₹${Math.round(n || 0)}`;

const PlatformDot = ({ platform }) => (
  <span
    className="w-1.5 h-1.5 rounded-full shrink-0"
    style={{ backgroundColor: platform === 'META' ? '#1877F2' : '#EA4335' }}
    title={platform === 'META' ? 'Meta Ads' : 'Google Ads'}
  />
);

// Score reads faster as a bar than a number, and keeps the row scannable at a glance.
const HealthBar = ({ score, color }) => (
  <div className="flex items-center gap-2">
    <div className="w-14 h-1 rounded-full bg-white/10 overflow-hidden">
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${Math.max(0, Math.min(100, score || 0))}%`, backgroundColor: color }}
      />
    </div>
    <span className="font-mono text-xs tracking-wide opacity-80 tabular-nums">{Math.round(score || 0)}</span>
  </div>
);

export default function AccountsTable({ accounts = [], onSelectAccount }) {
  const [query, setQuery] = useState('');
  const [platform, setPlatform] = useState('all');
  const [sort, setSort] = useState({ key: 'spend', dir: 'desc' });
  const [open, setOpen] = useState(() =>
    Object.fromEntries(STATUS_GROUPS.map((g) => [g.key, g.defaultOpen]))
  );

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return accounts
      .filter((a) => (platform === 'all' ? true : a.platform === platform))
      .filter((a) => (q ? (a.name || '').toLowerCase().includes(q) : true))
      .map((a) => ({
        ...a,
        spend: a.metrics?.spend || 0,
        leads: a.metrics?.leads || 0,
        cpl: a.metrics?.cpl || 0,
      }));
  }, [accounts, query, platform]);

  const sorted = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sort.key);
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) =>
      col && !col.numeric
        ? factor * String(a[sort.key] || '').localeCompare(String(b[sort.key] || ''))
        : factor * ((a[sort.key] || 0) - (b[sort.key] || 0))
    );
  }, [rows, sort]);

  const grouped = useMemo(() => {
    const buckets = Object.fromEntries(STATUS_GROUPS.map((g) => [g.key, []]));
    sorted.forEach((a) => {
      const key = buckets[a.health_status] ? a.health_status : 'healthy';
      buckets[key].push(a);
    });
    return buckets;
  }, [sorted]);

  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const counts = { all: accounts.length, META: 0, GOOGLE: 0 };
  accounts.forEach((a) => { if (counts[a.platform] !== undefined) counts[a.platform] += 1; });

  return (
    <div className="rounded-xl border border-white/10 bg-[#09090B] overflow-hidden">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border-b border-white/10">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search accounts…"
            aria-label="Search accounts by name"
            className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-[#6366f1]/60 transition-colors"
          />
        </div>
        <div className="flex items-center gap-1 shrink-0" role="group" aria-label="Filter by platform">
          {[
            { id: 'all', label: `All ${counts.all}` },
            { id: 'META', label: `Meta ${counts.META}` },
            { id: 'GOOGLE', label: `Google ${counts.GOOGLE}` },
          ].map((chip) => (
            <button
              key={chip.id}
              onClick={() => setPlatform(chip.id)}
              aria-pressed={platform === chip.id}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                platform === chip.id
                  ? 'bg-[#6366f1]/20 text-[#818cf8] border border-[#6366f1]/40'
                  : 'text-zinc-400 hover:text-white hover:bg-white/5 border border-transparent'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      {/* Column headers */}
      <div className="hidden md:grid grid-cols-[minmax(0,1fr)_120px_100px_80px_90px_100px] gap-3 px-4 py-2 border-b border-white/10 bg-white/[0.02]">
        {COLUMNS.map((col) => (
          <button
            key={col.key}
            onClick={() => toggleSort(col.key)}
            className={`flex items-center gap-1 text-[11px] uppercase tracking-wider transition-colors ${
              col.numeric ? 'justify-end' : 'justify-start'
            } ${sort.key === col.key ? 'text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
          >
            {col.label}
            <ArrowUpDown className="w-3 h-3 opacity-60" />
          </button>
        ))}
      </div>

      {/* Grouped rows */}
      <div className="max-h-[520px] overflow-y-auto">
        {STATUS_GROUPS.map(({ key, label, color, Icon }) => {
          const bucket = grouped[key] || [];
          if (!bucket.length) return null;
          const isOpen = open[key];
          return (
            <div key={key}>
              <button
                onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))}
                aria-expanded={isOpen}
                className="w-full flex items-center gap-2 px-4 py-2 bg-white/[0.03] hover:bg-white/[0.06] transition-colors border-b border-white/5 sticky top-0 z-10 backdrop-blur-xl"
              >
                {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
                <Icon className="w-4 h-4" style={{ color }} />
                <span className="text-sm font-semibold text-zinc-200">{label}</span>
                <span className="font-mono text-xs tracking-wide opacity-80 text-zinc-400">{bucket.length}</span>
              </button>

              {isOpen &&
                bucket.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => onSelectAccount && onSelectAccount(a)}
                    className="w-full grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1fr)_120px_100px_80px_90px_100px] gap-3 items-center px-4 py-2.5 text-left border-b border-white/5 hover:bg-white/[0.04] transition-colors"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <PlatformDot platform={a.platform} />
                      <span className="truncate text-sm text-zinc-100">{a.name}</span>
                    </span>
                    <span className="hidden md:flex justify-end">
                      <HealthBar score={a.health_score} color={color} />
                    </span>
                    <span className="font-mono text-xs tracking-wide opacity-80 text-right text-zinc-200 tabular-nums">
                      {inr(a.spend)}
                    </span>
                    <span className="hidden md:block font-mono text-xs tracking-wide opacity-80 text-right text-zinc-300 tabular-nums">
                      {a.leads}
                    </span>
                    <span className="hidden md:block font-mono text-xs tracking-wide opacity-80 text-right text-zinc-300 tabular-nums">
                      {a.cpl ? inr(a.cpl) : '—'}
                    </span>
                    <span className="hidden md:block font-mono text-xs tracking-wide opacity-80 text-right text-zinc-500 tabular-nums">
                      {a.campaign_count}
                    </span>
                  </button>
                ))}
            </div>
          );
        })}

        {!sorted.length && (
          <div className="px-4 py-10 text-center">
            <p className="text-sm text-zinc-400">No accounts match “{query}”.</p>
            <button onClick={() => { setQuery(''); setPlatform('all'); }} className="mt-2 text-xs text-[#818cf8] hover:underline">
              Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
